import {DropdownItemProps, SemanticCOLORS, SemanticICONS} from "semantic-ui-react";
import Icon, {IconSizeProp} from "semantic-ui-react/dist/commonjs/elements/Icon/Icon";
import {EOptionState} from "../../../DerivateOptionBar";
import React from "react";
import DataAdapterOptionsView from "../../../../views/OptionsView/DataAdapterOptionsView";
import {t} from "i18next";
import {AssignedModeltypeBasicDto, AssignedModeltypeBasicDtoAssignmentType, ModeltypeBasicDto} from "../../../../services/Client";
import _ from "lodash";

export type TDerivateIconProps = {
    name: SemanticICONS,
    color: SemanticCOLORS,
    size: IconSizeProp,
    supIconValue?: string
};

// draft types
export const DerivateAssignmentDraftTypes = new Set([
    AssignedModeltypeBasicDtoAssignmentType.WE,
    AssignedModeltypeBasicDtoAssignmentType.OE,
    AssignedModeltypeBasicDtoAssignmentType.SE
]);

// editable types
export const DerivateAssignmentEditableTypes = [
    EOptionState.Available,
    EOptionState.AvailableDraft,
    EOptionState.Optional,
    EOptionState.OptionalDraft,
    EOptionState.Standard,
    EOptionState.StandardDraft
];

// filter values for Basic Market PSLs
export const DerivateAssignmentFilterTypesBasicMarket = [
    ...DerivateAssignmentEditableTypes
];

// filter values for Specific Market PSLs
export const DerivateAssignmentFilterTypesSpecificMarket = [
    ...DerivateAssignmentEditableTypes,
    EOptionState.OptionalF,
    EOptionState.StandardC,
    EOptionState.StandardG,
    EOptionState.StandardS,
    EOptionState.StandardV,
    EOptionState.StandardB,
];

// used for assignment changes
export const DerivateAssignmentUpdateTypes = [
    EOptionState.NotAvailable,
    ...DerivateAssignmentEditableTypes
];

// editable types
export const DerivateDraftAssignmentUpdateTypes = [
    EOptionState.NotAvailable,
    EOptionState.AvailableDraft,
    EOptionState.OptionalDraft,
    EOptionState.StandardDraft
];

export class DerivateUtils {

    static getDerivateAssignmentIconProps(key: number, missingPDiPAsgn?: boolean): TDerivateIconProps {

        let derivateIconProps: TDerivateIconProps = {
            name: "close",
            color: missingPDiPAsgn ? "red" : "black",
            size: "large"
        };

        switch (key) {
            case EOptionState.Available:
                derivateIconProps.name = "square outline";
                break;
            case EOptionState.Optional:
                derivateIconProps.name = "circle outline";
                break;
            case EOptionState.Standard:
                derivateIconProps.name = "circle";
                break;
            case EOptionState.AvailableDraft:
                derivateIconProps.name = "square outline";
                derivateIconProps.color = "grey";
                derivateIconProps.size = "small";
                break;
            case EOptionState.OptionalDraft:
                derivateIconProps.name = "circle outline";
                derivateIconProps.color = "grey";
                derivateIconProps.size = "small";
                break;
            case EOptionState.StandardDraft:
                derivateIconProps.name = "circle";
                derivateIconProps.color = "grey";
                derivateIconProps.size = "small";
                break;
            case EOptionState.StandardC:
                derivateIconProps.name = "circle";
                derivateIconProps.supIconValue = "C";
                break;
            case EOptionState.StandardG:
                derivateIconProps.name = "circle";
                derivateIconProps.supIconValue = "G";
                break;
            case EOptionState.StandardS:
                derivateIconProps.name = "circle";
                derivateIconProps.supIconValue = "S";
                break;
            case EOptionState.StandardV:
                derivateIconProps.name = "circle";
                derivateIconProps.supIconValue = "V";
                break;
            case EOptionState.StandardB:
                derivateIconProps.name = "circle";
                derivateIconProps.supIconValue = "B";
                break;
            case EOptionState.OptionalF:
                derivateIconProps.name = "circle outline";
                derivateIconProps.supIconValue = "F";
                break;
            default:
                break;
        }

        return derivateIconProps;
    }

    static getDerivateRenderDataSource(typesDataSource: EOptionState[]) {
        return typesDataSource.map((val) => {
            const derivateIconProps: TDerivateIconProps = DerivateUtils.getDerivateAssignmentIconProps(val);
            const optionTypeItem: DropdownItemProps = {
                value: val,
                key: val,
                className: "derivateIconDropdown",
                icon:
                    (
                        <>
                            <Icon
                                className={derivateIconProps.name === "close" ? "invisible" : "derivateIcon"}
                                name={derivateIconProps.name}
                                size={derivateIconProps.size === "small" ? "tiny" : "small"}
                                color={derivateIconProps.color}
                            />
                            <sup className={"font-weight--bold"}>{derivateIconProps.supIconValue}</sup>
                        </>
                    ),
                text: val === EOptionState.NotAvailable ? "" : '(' + t("OPTIONS_VIEW:" + DataAdapterOptionsView.getAssigment(val).toString()) + ')'
            };

            return optionTypeItem;
        }
        );
    }

    static renderKeyDerivate(modeltypeBasic: ModeltypeBasicDto, userLanguage: string): string {
        return this.renderDerivateName(modeltypeBasic, userLanguage) + '(' + modeltypeBasic.btyp + ')';
    }

    static renderDerivateName(modeltypeBasic: ModeltypeBasicDto, userLanguage: string): string | undefined {
        const nameDto = _.find(modeltypeBasic.names, dto => dto.language && dto.language.code === userLanguage);
        return nameDto && nameDto.name ? nameDto.name : undefined;
    }

    static compareBySequenceAndBtyp(a: ModeltypeBasicDto, b: ModeltypeBasicDto) {
        // @ts-ignore
        return a.intSequence - b.intSequence
            || (a.btyp === undefined ? 1
                : b.btyp === undefined ? -1
                    : a.btyp.localeCompare(b.btyp, 'en')
            );
    }

    static compareByBtyp(a: ModeltypeBasicDto, b: ModeltypeBasicDto) {
        return a.btyp === undefined ? 1
            : b.btyp === undefined ? -1
                : a.btyp.localeCompare(b.btyp, 'en');
    }

    static getAMTBIcon = (amtbDtos: AssignedModeltypeBasicDto[], mtb: ModeltypeBasicDto) => {
        const amtbType = amtbDtos.find((dto) => dto.modeltypeBasic.objectId === mtb.objectId);
        let derivateIconProps;
        if (amtbType) {
            derivateIconProps = DerivateUtils.getDerivateAssignmentIconProps(DataAdapterOptionsView.getOptionState(amtbType.assignmentType));
        } else {
            derivateIconProps = DerivateUtils.getDerivateAssignmentIconProps(0);
        }

        return (
          <>
            <Icon
              className={derivateIconProps.name === "close" ? "invisible" : ""}
              name={derivateIconProps.name}
              size={derivateIconProps.size}
              color={derivateIconProps.color}
            />
            {derivateIconProps.supIconValue && (
              <sup className={"font-weight--bold"}>
                {derivateIconProps.supIconValue}
              </sup>
            )}
          </>
        );
    }

    static getPreviewAMTBIcon = (amtbDtos: AssignedModeltypeBasicDto[], mtb: ModeltypeBasicDto, isLastStep: boolean) => {
        const amtbType = amtbDtos.find((dto) => dto.modeltypeBasic.objectId === mtb.objectId);
        let derivateIconProps;
        let draftAssignment: string = '';
        if (amtbType) {
            switch (amtbType.assignmentType.toUpperCase()) {
                case AssignedModeltypeBasicDtoAssignmentType.W:
                    draftAssignment = AssignedModeltypeBasicDtoAssignmentType.WE.toLowerCase();
                    break;
                case AssignedModeltypeBasicDtoAssignmentType.WE:
                    draftAssignment = AssignedModeltypeBasicDtoAssignmentType.WE.toLowerCase();
                    break;
                case AssignedModeltypeBasicDtoAssignmentType.O:
                    draftAssignment = AssignedModeltypeBasicDtoAssignmentType.OE.toLowerCase();
                    break;
                case AssignedModeltypeBasicDtoAssignmentType.OE:
                    draftAssignment = AssignedModeltypeBasicDtoAssignmentType.OE.toLowerCase();
                    break;
                case AssignedModeltypeBasicDtoAssignmentType.S:
                    draftAssignment = AssignedModeltypeBasicDtoAssignmentType.SE.toLowerCase();
                    break;
                case AssignedModeltypeBasicDtoAssignmentType.SE:
                    draftAssignment = AssignedModeltypeBasicDtoAssignmentType.SE.toLowerCase();
                    break;
                case AssignedModeltypeBasicDtoAssignmentType.SG:
                case AssignedModeltypeBasicDtoAssignmentType.SB:
                case AssignedModeltypeBasicDtoAssignmentType.SV:
                case AssignedModeltypeBasicDtoAssignmentType.SC:
                case AssignedModeltypeBasicDtoAssignmentType.SS:
                    // S - if workflow not in last step, SE - if workflow is  in last step
                    if(isLastStep) {
                        draftAssignment = AssignedModeltypeBasicDtoAssignmentType.SE.toLowerCase(); 
                    } else {
                        draftAssignment = AssignedModeltypeBasicDtoAssignmentType.S.toLowerCase(); 
                    }
                    break; 
                case AssignedModeltypeBasicDtoAssignmentType.OF:
                     // O - if workflow not in last step, OE - if workflow is  in last step
                     if(isLastStep) {
                        draftAssignment = AssignedModeltypeBasicDtoAssignmentType.OE.toLowerCase(); 
                    } else {
                        draftAssignment = AssignedModeltypeBasicDtoAssignmentType.O.toLowerCase(); 
                    }
                     break; 
                default:
                    break;
            }
            derivateIconProps = DerivateUtils.getDerivateAssignmentIconProps(DataAdapterOptionsView.getOptionState(draftAssignment));
        } else {
            derivateIconProps = DerivateUtils.getDerivateAssignmentIconProps(0);
        }

        return (
            <Icon
                className={derivateIconProps.name === "close" ? "invisible" : ''}
                name={derivateIconProps.name}
                size={derivateIconProps.size}
                color={derivateIconProps.color}
            />
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/Table/components/derivate/DerivateUtils.tsx